import type { CaptionCue, WordTiming } from '@slideforge/shared';

const MAX_LINE = 42;
const MAX_CUE_CHARS = MAX_LINE * 2;
const MIN_CUE_MS = 1_000;
const MAX_CUE_MS = 7_000;

/** Builds readable WCAG-oriented cues from provider word timings. */
export function captionsFromWordTimings(text: string, words: readonly WordTiming[]): readonly CaptionCue[] {
  if (words.length === 0) return [];

  const groups: WordTiming[][] = [];
  let current: WordTiming[] = [];
  for (const word of words) {
    const candidate = [...current, word];
    const candidateText = cueText(text, candidate);
    const duration = word.endMs - (current[0]?.startMs ?? word.startMs);
    if (current.length > 0 && (candidateText.length > MAX_CUE_CHARS || duration > MAX_CUE_MS)) {
      groups.push(current);
      current = [word];
    } else {
      current = candidate;
    }
  }
  if (current.length > 0) groups.push(current);

  // A very short tail reads as a flash. Merge it when doing so keeps both hard limits.
  if (groups.length > 1) {
    const tail = groups[groups.length - 1] as WordTiming[];
    const before = groups[groups.length - 2] as WordTiming[];
    const combined = [...before, ...tail];
    const first = combined[0] as WordTiming;
    const last = combined[combined.length - 1] as WordTiming;
    if (
      (tail[tail.length - 1]?.endMs ?? 0) - (tail[0]?.startMs ?? 0) < MIN_CUE_MS &&
      cueText(text, combined).length <= MAX_CUE_CHARS &&
      last.endMs - first.startMs <= MAX_CUE_MS
    ) {
      groups.splice(groups.length - 2, 2, combined);
    }
  }

  return groups.map((group, index) => {
    const first = group[0] as WordTiming;
    const last = group[group.length - 1] as WordTiming;
    const nextStart = groups[index + 1]?.[0]?.startMs;
    const desiredEnd = Math.max(last.endMs, first.startMs + MIN_CUE_MS);
    const endMs = Math.min(first.startMs + MAX_CUE_MS, nextStart ?? desiredEnd, desiredEnd);
    return {
      startMs: first.startMs,
      endMs: Math.max(last.endMs, endMs),
      text: wrapCue(cueText(text, group)),
    };
  });
}

export function toWebVtt(cues: readonly CaptionCue[]): string {
  const body = cues
    .map((cue, index) => `${index + 1}\n${vttTime(cue.startMs)} --> ${vttTime(cue.endMs)}\n${cue.text}`)
    .join('\n\n');
  return `WEBVTT\n\n${body}${body ? '\n' : ''}`;
}

function cueText(text: string, words: readonly WordTiming[]): string {
  const first = words[0];
  const last = words[words.length - 1];
  return first && last ? text.slice(first.charStart, last.charEnd).replace(/\s+/gu, ' ').trim() : '';
}

function wrapCue(text: string): string {
  if (text.length <= MAX_LINE) return text;
  let split = text.lastIndexOf(' ', MAX_LINE);
  if (split <= 0) split = text.indexOf(' ', MAX_LINE);
  if (split <= 0) return text;
  return `${text.slice(0, split)}\n${text.slice(split + 1)}`;
}

function vttTime(milliseconds: number): string {
  const total = Math.max(0, Math.round(milliseconds));
  const hours = Math.floor(total / 3_600_000);
  const minutes = Math.floor((total % 3_600_000) / 60_000);
  const seconds = Math.floor((total % 60_000) / 1_000);
  const ms = total % 1_000;
  return `${pad(hours, 2)}:${pad(minutes, 2)}:${pad(seconds, 2)}.${pad(ms, 3)}`;
}

function pad(value: number, width: number): string {
  return String(value).padStart(width, '0');
}
